import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Eye, Type, Moon, Sun, Volume2, VolumeX, RotateCcw, X, Accessibility } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    reduceMotion: false,
    highlightLinks: false,
    largePointer: false,
    readingGuide: false,
    dyslexiaFont: false,
  });

  // Load saved settings on mount - intentionally synchronous for initial state
  useEffect(() => {
    const saved = localStorage.getItem('a11y_settings');
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        setSettings(parsedSettings);
      } catch {
        // Invalid JSON, use defaults
      }
    }
  }, []);

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${settings.fontSize}%`;
    
    // Contrast mode
    root.classList.remove('high-contrast', 'inverted');
    if (settings.contrast === 'high') {
      root.classList.add('high-contrast');
    } else if (settings.contrast === 'inverted') {
      root.classList.add('inverted');
    }
    
    // Reduce motion
    if (settings.reduceMotion) {
      root.style.setProperty('--animation-duration', '0s');
      root.classList.add('reduce-motion');
    } else {
      root.style.removeProperty('--animation-duration');
      root.classList.remove('reduce-motion');
    }
    
    // Highlight links
    if (settings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
    
    // Large pointer
    if (settings.largePointer) {
      root.classList.add('large-pointer');
    } else {
      root.classList.remove('large-pointer');
    }
    
    // Dyslexia font
    if (settings.dyslexiaFont) {
      root.classList.add('dyslexia-font');
    } else {
      root.classList.remove('dyslexia-font');
    }
    
    // Save settings
    localStorage.setItem('a11y_settings', JSON.stringify(settings));
  }, [settings]);

  // Add styles to document head
  useEffect(() => {
    const styleId = 'a11y-styles';
    let styleEl = document.getElementById(styleId);
    
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    styleEl.textContent = `
      /* High Contrast Mode */
      .high-contrast {
        filter: contrast(1.5) !important;
      }
      .high-contrast * {
        border-color: #fff !important;
      }
      
      /* Inverted Colors */
      .inverted {
        filter: invert(1) hue-rotate(180deg) !important;
      }
      .inverted img, .inverted video {
        filter: invert(1) hue-rotate(180deg) !important;
      }
      
      /* Reduce Motion */
      .reduce-motion, .reduce-motion * {
        animation-duration: 0.001s !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001s !important;
        scroll-behavior: auto !important;
      }
      
      /* Highlight Links */
      .highlight-links a {
        background-color: yellow !important;
        color: black !important;
        padding: 2px 4px !important;
        border-radius: 2px !important;
      }
      
      /* Large Pointer */
      .large-pointer, .large-pointer * {
        cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' stroke='%23fff' stroke-width='1' d='M5.5 3.21V20.8l5.5-5.5h7.5L5.5 3.21z'/%3E%3C/svg%3E") 0 0, auto !important;
      }
      
      /* Dyslexia Friendly Font */
      .dyslexia-font, .dyslexia-font * {
        font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
        letter-spacing: 0.1em !important;
        word-spacing: 0.2em !important;
        line-height: 1.8 !important;
      }
      
      /* Reading Guide */
      .reading-guide-line {
        position: fixed;
        left: 0;
        right: 0;
        height: 2px;
        background: rgba(255, 0, 0, 0.5);
        pointer-events: none;
        z-index: 99999;
      }
    `;
    
    return () => {
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    };
  }, []);

  // Reading guide mouse follower
  useEffect(() => {
    const guideId = 'reading-guide-line';
    let guideEl = document.getElementById(guideId);
    
    if (settings.readingGuide) {
      if (!guideEl) {
        guideEl = document.createElement('div');
        guideEl.id = guideId;
        guideEl.className = 'reading-guide-line';
        document.body.appendChild(guideEl);
      }
      
      const handleMouseMove = (e) => {
        guideEl.style.top = `${e.clientY}px`;
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        if (guideEl && guideEl.parentNode) {
          guideEl.parentNode.removeChild(guideEl);
        }
      };
    } else {
      if (guideEl && guideEl.parentNode) {
        guideEl.parentNode.removeChild(guideEl);
      }
    }
  }, [settings.readingGuide]);

  // Esc-to-close (WCAG 2.1.2, no keyboard trap)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        setIsOpen(false);
      }
    };
    // capture phase + window so child handlers can't swallow it
    window.addEventListener('keydown', handler, true);
    document.addEventListener('keydown', handler, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
      document.removeEventListener('keydown', handler, true);
    };
  }, [isOpen]);

  const resetSettings = () => {
    setSettings({
      fontSize: 100,
      contrast: 'normal',
      reduceMotion: false,
      highlightLinks: false,
      largePointer: false,
      readingGuide: false,
      dyslexiaFont: false,
    });
  };

  // Don't show on admin routes
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/store') || path.startsWith('/employee')) {
      return null;
    }
  }

  return (
    <>
      {/* Toggle Button, bottom-right, above all other sticky CTAs. WCAG 2.5.5: 44x44 minimum. */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-[9.5rem] sm:bottom-[5.5rem] right-3 sm:right-6 z-[99997] w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 focus-visible:outline-4 focus-visible:outline-cyan-300 ${
          isOpen ? 'bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        aria-label={isOpen ? 'Close accessibility menu' : 'Open accessibility menu'}
        aria-expanded={isOpen}
        aria-controls="a11y-panel"
        data-testid="accessibility-toggle"
      >
        {isOpen ? (
          <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        ) : (
          <Accessibility className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        )}
      </button>

      {/* Settings Panel, rendered via Portal to document.body so it ALWAYS escapes any
          parent transform/filter that would otherwise break `position: fixed`. This was
          the root cause of the mobile panel not appearing for some users. */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <>
          {/* Mobile backdrop, taps outside close the panel */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close accessibility menu"
            className="sm:hidden fixed inset-0 z-[99998] bg-black/40 backdrop-blur-sm"
            data-testid="accessibility-backdrop"
          />
          <div
            id="a11y-panel"
            className="fixed z-[99999] bg-white shadow-2xl border border-gray-200 overflow-hidden flex flex-col
                       inset-x-0 bottom-0 rounded-t-2xl max-h-[85vh]
                       sm:inset-x-auto sm:bottom-[10rem] sm:right-6 sm:w-80 sm:rounded-2xl sm:max-h-[calc(100vh-12rem)]"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Accessibility Settings"
            data-testid="accessibility-panel"
          >
          {/* Defensive: prevent any global "[role=dialog] > div { min-height: 100vh }" rule from breaking layout */}
          <style>{`
            #a11y-panel > div { min-height: 0 !important; }
          `}</style>
          {/* Mobile drag handle (visual affordance) */}
          <div className="sm:hidden flex justify-center pt-2 pb-1">
            <span aria-hidden="true" className="w-12 h-1.5 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="bg-blue-600 px-4 py-3 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Accessibility className="w-5 h-5" />
                <h2 className="font-bold">Accessibility</h2>
              </div>
              <button 
                onClick={resetSettings}
                className="no-min-height text-sm flex items-center gap-1 hover:bg-blue-700 px-2 py-1 rounded"
                aria-label="Reset all settings"
                style={{ minHeight: 'auto' }}
              >
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>
          </div>

          {/* Settings (single scroll area) */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Font Size */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Type className="w-4 h-4" /> Font Size: {settings.fontSize}%
              </label>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setSettings(s => ({ ...s, fontSize: Math.max(80, s.fontSize - 10) }))}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-lg font-bold"
                  aria-label="Decrease font size"
                >
                  A-
                </button>
                <input
                  type="range"
                  min="80"
                  max="150"
                  step="10"
                  value={settings.fontSize}
                  onChange={(e) => setSettings(s => ({ ...s, fontSize: parseInt(e.target.value) }))}
                  className="flex-1"
                  aria-label="Font size slider"
                />
                <button 
                  onClick={() => setSettings(s => ({ ...s, fontSize: Math.min(150, s.fontSize + 10) }))}
                  className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-lg font-bold"
                  aria-label="Increase font size"
                >
                  A+
                </button>
              </div>
            </div>

            {/* Contrast */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Eye className="w-4 h-4" /> Contrast Mode
              </label>
              <div className="flex gap-2">
                {[
                  { id: 'normal', label: 'Normal' },
                  { id: 'high', label: 'High' },
                  { id: 'inverted', label: 'Inverted' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setSettings(s => ({ ...s, contrast: opt.id }))}
                    className={`flex-1 px-3 py-2 rounded text-sm ${
                      settings.contrast === opt.id 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                    aria-pressed={settings.contrast === opt.id}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-3">
              {[
                { key: 'reduceMotion', label: 'Reduce Motion', desc: 'Stop animations' },
                { key: 'highlightLinks', label: 'Highlight Links', desc: 'Make links visible' },
                { key: 'largePointer', label: 'Large Cursor', desc: 'Bigger mouse pointer' },
                { key: 'readingGuide', label: 'Reading Guide', desc: 'Line follows cursor' },
                { key: 'dyslexiaFont', label: 'Dyslexia Font', desc: 'Easier to read font' },
              ].map((opt) => (
                <label key={opt.key} className="flex items-center justify-between cursor-pointer group">
                  <div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600">{opt.label}</span>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={settings[opt.key]}
                    onClick={() => setSettings(s => ({ ...s, [opt.key]: !s[opt.key] }))}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      settings[opt.key] ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span 
                      className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        settings[opt.key] ? 'translate-x-6' : 'translate-x-0.5'
                      }`} 
                    />
                  </button>
                </label>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 bg-gray-50 border-t text-center">
            <p className="text-xs text-gray-500">
              Press <kbd className="px-1 bg-gray-200 rounded">Alt+A</kbd> to open
            </p>
          </div>
        </div>
        </>,
        document.body
      )}
    </>
  );
};

export default AccessibilityWidget;
